import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Grid, 
  Paper,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Alert
} from '@mui/material';
import { useSession } from '../../context/SessionContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getColorFromString } from '../../utils/colorUtils';

// Define common planning poker card values
const ALL_CARD_DECKS = {
  fibonacci: ['0', '1', '2', '3', '5', '8', '13', '21', '?'],
  'modified-fibonacci': ['0', '½', '1', '2', '3', '5', '8', '13', '20', '40', '100', '?'],
  't-shirt': ['XS', 'S', 'M', 'L', 'XL', 'XXL', '?'],
};

const VotePanel = ({ 
  activeItem, 
  currentVote,
  onVote, 
  onConfirmVote,
  votes, 
  participants, 
  isFacilitator, 
  isConnected,
  votedCount,
  totalVoters
}) => {
  console.log('VotePanel render - props: isFacilitator:', isFacilitator, 'votedCount:', votedCount, 'totalVoters:', totalVoters, 'activeItem:', activeItem);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { participant, session } = useSession();
  const [, setShowConfirmation] = useState(false);
  const [confirmedVote, setConfirmedVote] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  
  // Hooks must be called unconditionally, so useEffect is here, before any early returns.
  useEffect(() => {
    if (activeItem && confirmedVote && activeItem.id !== confirmedVote.itemId) {
        setShowConfirmation(false);
        setConfirmedVote(null);
    } else if (!currentVote) {
        setShowConfirmation(false);
        setConfirmedVote(null);
    }
  }, [activeItem, currentVote, confirmedVote]); // Added confirmedVote to dependency array as it's used logic within

  // Check if the current participant (could be facilitator) is set to not vote
  if (participant && participant.isVoting === false) {
    if (isFacilitator && (activeItem && activeItem.status === 'active')) { 
      return (
        <Paper elevation={2} sx={{ p: 2, mt: 2, textAlign: 'center', backgroundColor: theme.palette.background.paper }}>
          <Typography variant="body1" color="text.secondary">
            You have opted out of voting for this session.
          </Typography>
          {/* Still show voting progress for other participants */} 
          {isFacilitator && totalVoters > 0 && activeItem && activeItem.status === 'active' && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2, p: 1, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '4px' }}>
              <InfoOutlinedIcon color="action" sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Voting Progress: <strong>{votedCount} of {totalVoters}</strong> participants are voting.
              </Typography>
            </Box>
          )}
        </Paper>
      );
    } else if (!isFacilitator) {
      // This case should ideally not happen if participant.isVoting is only false for facilitators who opt out
      // but as a safeguard if a regular participant somehow gets isVoting: false
      return null; 
    }
  }
  
  // Touch interaction handlers for mobile
  const handleTouchStart = (e, value) => {
    if (isMobile) {
      setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      setSelectedCard(value);
      // Haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }
  };

  const handleTouchEnd = (e, value) => {
    if (!touchStart) return;

    const touchEnd = { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    const distance = Math.sqrt(
      Math.pow(touchEnd.x - touchStart.x, 2) + Math.pow(touchEnd.y - touchStart.y, 2)
    );

    // Only register vote if touch didn't move much (not a swipe)
    if (distance < 10) {
      handleVoteClick(value);
      if (navigator.vibrate) {
        navigator.vibrate(100); // Confirmation vibration
      }
    }

    setTouchStart(null);
    setSelectedCard(null);
  };

  const handleVoteClick = (value) => {
    if (!isConnected) {
      console.warn('Cannot vote: not connected');
      return;
    }
    if (confirmedVote === null || (activeItem && confirmedVote.itemId !== activeItem.id)) {
      onVote(value);
    }
  };
  
  const handleConfirmVote = () => {
    if (currentVote !== null && activeItem) {
      if (onConfirmVote) {
        onConfirmVote(activeItem.id || activeItem._id, currentVote);
        console.log('VotePanel: Vote confirmed by user:', currentVote, 'for item:', activeItem.id, 'onConfirmVote called.');
      } else {
        console.error('VotePanel: onConfirmVote prop is missing!');
      }
      setConfirmedVote({ value: currentVote, itemId: activeItem.id });
      setShowConfirmation(true);
    }
  };
  
  console.log('VotePanel - session object from context:', session);
  console.log('VotePanel - session.cardDeckType:', session?.cardDeckType);
  const cardDeck = ALL_CARD_DECKS[session?.cardDeckType] || ALL_CARD_DECKS.fibonacci;

  if (!activeItem || activeItem.status !== 'active') {
    if (activeItem && (activeItem.status === 'revealed' || activeItem.status === 'estimated')) {
      return (
        <Paper elevation={2} sx={{ p: 2, mt: 2, backgroundColor: theme.palette.background.paper }}>
          <Typography variant="h6" gutterBottom align="center">
            Voting Results for: {activeItem.title}
          </Typography>
          {activeItem.status === 'estimated' && activeItem.finalEstimate && (
            <Typography variant="h5" color="primary" align="center" gutterBottom>
              Final Estimate: {activeItem.finalEstimate}
            </Typography>
          )}
          <List dense>
            {votes && votes.length > 0 ? (
              votes.map((vote) => (
                <ListItem key={vote.participant._id || vote.participant.id}>
                  <ListItemAvatar>
                    <Avatar sx={{
                      bgcolor: getColorFromString(vote.participant.id || vote.participant._id || vote.participant.name),
                      color: theme.palette.getContrastText(getColorFromString(vote.participant.id || vote.participant._id || vote.participant.name))
                    }}>
                      {vote.participant.name.substring(0, 1).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={vote.participant.name} 
                    secondary={`Voted: ${vote.value}`}
                  />
                </ListItem>
              ))
            ) : (
              <Typography align="center" color="text.secondary">No votes recorded for this round.</Typography>
            )}
          </List>
        </Paper>
      );
    }
    return null;
  }

  const hasConfirmedCurrentItem = confirmedVote !== null && activeItem && confirmedVote.itemId === activeItem.id;

  return (
    <Box sx={{ mt: 2 }}>
      {/* Item Title and Voting Progress Section */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: '#141414', borderRadius: '4px', border: '1px solid #2a2a2a' }}>
        <Typography variant="h6" gutterBottom component="div">
          Voting on: <Typography component="span" variant="h6" fontWeight="bold">{activeItem.title}</Typography>
        </Typography>

        {isFacilitator && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, p: 1, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '4px' }}>
            <InfoOutlinedIcon color="action" sx={{ mr: 1 }} />
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              Voting Progress: <strong>{votedCount} of {totalVoters}</strong> participants have voted.
            </Typography>
          </Box>
        )}
        {!isFacilitator && totalVoters > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, p: 1, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '4px' }}>
            <InfoOutlinedIcon color="action" sx={{ mr: 1 }} />
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              Voting Progress: <strong>{votedCount} of {totalVoters}</strong> voted.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Voting Cards Grid */}
      <Typography variant="subtitle1" gutterBottom sx={{ textAlign: 'center', mb: 1.5 }}>
        Select your estimate:
      </Typography>
      <Grid container spacing={1.5} justifyContent="center">
        {cardDeck.map((value) => (
          <Grid item key={value} xs={4} sm={3} md={2}>
            <Button
              fullWidth
              variant={(currentVote === value && !hasConfirmedCurrentItem) || (hasConfirmedCurrentItem && confirmedVote.value === value) ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => !isMobile && handleVoteClick(value)}
              onTouchStart={(e) => handleTouchStart(e, value)}
              onTouchEnd={(e) => handleTouchEnd(e, value)}
              disabled={hasConfirmedCurrentItem || !isConnected}
              sx={{
                minHeight: isMobile ? '50px' : '60px',
                fontSize: isMobile ? '1rem' : '1.2rem',
                fontWeight: 'bold',
                opacity: (hasConfirmedCurrentItem && confirmedVote.value !== value) ? 0.5 : 1,
                transform: selectedCard === value ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.2s ease',
                // Touch-specific styles
                touchAction: 'manipulation',
                userSelect: 'none',
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              {value}
            </Button>
          </Grid>
        ))}
      </Grid>

      {currentVote !== null && !hasConfirmedCurrentItem && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="subtitle1" sx={{ mb: 1}}>
            You selected: <strong>{currentVote}</strong>
          </Typography>
          <Button 
            variant="contained" 
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={handleConfirmVote}
            disabled={!isConnected}
          >
            Confirm Your Vote
          </Button>
        </Box>
      )}

      {hasConfirmedCurrentItem && confirmedVote && (
        <Box sx={{ mt: 3, p: 2, textAlign: 'center', backgroundColor: 'rgba(34, 197, 94, 0.15)', borderRadius: '4px', border: '1px solid #22c55e' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 30, mb: 1 }} />
          <Typography variant="h6" color="success.light">
            You voted: {confirmedVote.value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your vote is locked. Waiting for others.
          </Typography>
        </Box>
      )}

      {!isConnected && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          You are not connected. Please check your connection to vote.
        </Alert>
      )}
    </Box>
  );
};

export default VotePanel; 